import {
    Box,
    Button,
    Checkbox,
    ClickAwayListener,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Popper,
    Select,
    TextField,
    Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"

import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DateTimePicker } from "@mui/x-date-pickers";

import dayjs from 'dayjs'

import ConfirmationDialogCaptcha from "../Dialogs/ConfimationDialogCaptcha"
import NewCustomFieldForm from "./NewCustomFieldForm";

export const CustomFieldInput = ({ field, value, updateCustomFieldValue }) => {
    switch (field.field_type) {
        case "text":
            return (
                <TextField
                    key={field.id}
                    size="small"
                    label={field.name}
                    placeholder={field.placeholder || `Digite ${field.name.toLowerCase()}`}
                    value={value}
                    required={field.required}
                    onChange={(event) => updateCustomFieldValue(field.id, event.target.value)}
                    inputProps={{ maxLength: 255 }}
                />
            )
        case "textarea":
            return (
                <TextField
                    key={field.id}
                    size="small"
                    label={field.name}
                    placeholder={field.placeholder || `Digite ${field.name.toLowerCase()}`}
                    value={value}
                    required={field.required}
                    multiline
                    rows={3}
                    onChange={(event) => updateCustomFieldValue(field.id, event.target.value)}
                    inputProps={{ maxLength: 1024 }}
                />
            )
        case "number":
            return (
                <TextField
                    key={field.id}
                    size="small"
                    label={field.name}
                    placeholder={field.placeholder || "Digite um número"}
                    type="number"
                    value={value}
                    required={field.required}
                    onChange={(event) => updateCustomFieldValue(field.id, event.target.value)}
                />
            )
        case "select":
            return (
                <FormControl key={field.id} size="small" fullWidth>
                    <InputLabel>{field.name}</InputLabel>
                    <Select
                        value={value}
                        label={field.name}
                        required={field.required}
                        onChange={(event) => updateCustomFieldValue(field.id, event.target.value)}
                    >
                        {field.options?.map((option, index) => (
                            <MenuItem key={`custom-field-${field.id}-${index}`} value={option.value}>{option.value}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )
        case "date":
            return (
                <DemoContainer key={field.id} components={['DatePicker']} sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingTop: 0,
                    overflow: "unset"
                }}>
                    <Box>{field.name}{field.required && ' *'}</Box>
                    <DateTimePicker
                        sx={{ minWidth: "8rem" }}
                        views={['day', 'month', 'year']}
                        format={"DD/MM/YYYY"}
                        slotProps={{ textField: { size: "small", sx: { width: "60%" }, required: field.required } }}
                        value={value ? dayjs(value) : null}
                        onChange={(event) => updateCustomFieldValue(field.id, event ? event.format() : "")}
                    />
                </DemoContainer>
            )
        case "checkbox":
            return (
                <Box key={field.id} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                        checked={value === true || value === "true" || value === "True"}
                        onChange={(event) => updateCustomFieldValue(field.id, event.target.checked)}
                    />
                    <Typography>{field.name}{field.required && ' *'}</Typography>
                </Box>
            )
        default:
            return null
    }
}

const getFieldTypeLabel = (fieldType) => (
    fieldType === "text"
        ? "Texto"
        : fieldType === "textarea"
        ? "Texto longo"
        : fieldType === "number"
        ? "Número"
        : fieldType === "select"
        ? "Lista"
        : fieldType === "date"
        ? "Data"
        : fieldType === "checkbox"
        ? "Checkbox"
        : fieldType
)

const OpportunityCustomFieldsSection = ({
    allCustomFields,
    customFieldsOpen,
    deleteCustomField,
    editingCustomField,
    handleEditCustomField,
    handleSaveCustomField,
    newCustomFieldsRef,
    selectOpen,
    setCustomFieldsOpen,
    setEditingCustomField,
    setSelectOpen,
    setShowDeleteCustomFieldConfirmation,
    setShowNewCustomFieldForm,
    showDeleteCustomFieldConfirmation,
    showNewCustomFieldForm,
    state,
    updateCustomFieldValue,
}) => (
    <Box className="custom-fields-container">
        <Box
            className="custom-fields-header"
            sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
            }}
        >
            <Typography variant="h6" sx={{ marginRight: "auto" }}>
                Campos Personalizados
            </Typography>
            <IconButton
                ref={newCustomFieldsRef}
                onClick={() => setCustomFieldsOpen((prev) => !prev)}
            >
                <AddIcon />
            </IconButton>
        </Box>

        {/* Render existing custom fields */}
        <Box
            className="custom-fields-list"
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
            }}
        >
            {allCustomFields.map((field) =>
                <CustomFieldInput
                    key={field.id}
                    field={field}
                    value={state.custom_fields[field.id] || ""}
                    updateCustomFieldValue={updateCustomFieldValue}
                />
            )}
        </Box>

        <Popper
            open={customFieldsOpen}
            anchorEl={newCustomFieldsRef.current}
            placement="top-start"
            sx={{ zIndex: 1300 }}
        >
            {/* <Popper open={customFieldsOpen} anchorEl={newCustomFieldsRef.current} placement="top-start"> */}
            <ClickAwayListener
                onClickAway={(event) => {
                    // Don't close if Select is open
                    if (selectOpen) {
                        return;
                    }
                    setCustomFieldsOpen(false);
                }}
            >
                <Paper>
                    {!showNewCustomFieldForm && (
                        <Box
                            className="opportunity-custom-fields-container"
                            sx={{ padding: "1rem", minWidth: "300px" }}
                        >
                            {allCustomFields.length === 0 && (
                                <Box sx={{ padding: "1rem" }}>
                                    Jornada sem campos personalizados.
                                </Box>
                            )}
                            {allCustomFields.length > 0 && (
                                <Box sx={{ marginBottom: "1rem" }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            marginBottom: "0.5rem",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        Campos existentes:
                                    </Typography>
                                    {allCustomFields.map((field) => (
                                        <Box
                                            key={field.id}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                padding: "0.25rem 0",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "text.secondary",
                                                    flex: 1,
                                                }}
                                            >
                                                • {field.name} ({getFieldTypeLabel(field.field_type)}){field.required && " - Obrigatório"}{field.show_in_summary && " - Resumo"}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => {
                                                    setEditingCustomField(field)
                                                    setShowNewCustomFieldForm(true)
                                                }}
                                                sx={{ ml: 1 }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                    setShowDeleteCustomFieldConfirmation(
                                                        field
                                                    )
                                                }
                                                sx={{ ml: 1 }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            <Button
                                className="opportunity-btn"
                                onClick={() => {
                                    setEditingCustomField(null)
                                    setShowNewCustomFieldForm(true)
                                }}
                            >
                                <AddIcon />
                                Criar campo personalizado
                            </Button>
                        </Box>
                    )}
                    {showNewCustomFieldForm && (
                        <NewCustomFieldForm
                            initialField={editingCustomField}
                            onBack={() => {
                                setEditingCustomField(null)
                                setShowNewCustomFieldForm(false)
                            }}
                            onSave={editingCustomField ? handleEditCustomField : handleSaveCustomField}
                            onSelectOpenChange={(isOpen) => setSelectOpen(isOpen)}
                        />
                    )}
                    {showDeleteCustomFieldConfirmation && (
                        <ConfirmationDialogCaptcha
                            open={showDeleteCustomFieldConfirmation}
                            title="Confirmação"
                            description={
                                <Box>
                                    Para remover o campo personalizado "
                                    {showDeleteCustomFieldConfirmation.name}", digite:{" "}
                                    <span style={{ fontWeight: "bold" }}>remover</span> no campo
                                    abaixo.
                                </Box>
                            }
                            handleClose={() => setShowDeleteCustomFieldConfirmation(null)}
                            handleSubmit={() => {
                                deleteCustomField(showDeleteCustomFieldConfirmation.id);
                                setShowDeleteCustomFieldConfirmation(null);
                            }}
                            captcha={"remover"}
                        />
                    )}
                </Paper>
            </ClickAwayListener>
        </Popper>
    </Box>
)

export default OpportunityCustomFieldsSection
